import { Controller } from '@hotwired/stimulus'
import { createTimeline, cubicBezier } from 'animejs'

export default class extends Controller {
  static targets = [
    'acceptAll',
    'birthYear',
    'button',
    'didYouMean',
    'disposable',
    'domain',
    'examplePrompt',
    'free',
    'fullName',
    'gender',
    'input',
    'mailboxFull',
    'meterMarker',
    'meterScore',
    'mxRecord',
    'noReply',
    'rateLimitPrompt',
    'reason',
    'results',
    'resultsForText',
    'resultsInner',
    'resultItemText',
    'role',
    'smtpProvider',
    'state',
    'tag',
    'user',
    'captcha'
  ]

  static values = {
    apiKey: String,
    resultsExpanded: { type: Boolean, default: false },
    state: { type: String, default: 'unknown' },
    translations: Object
  }

  connect() {
    this.response = null
    this.tippyOptions = {
      allowHTML: true,
      animation: 'shift-away',
      duration: 150,
      popperOptions: {
        modifiers: [
          {
            name: 'flip',
            options: { fallbackPlacements: ['bottom', 'top'] }
          }
        ]
      }
    }
  }

  clearResults() {
    ;[...this.stateTargets, ...this.reasonTargets].forEach(target => {
      target.hidden = true
      target.ariaHidden = true
    })

    this.resultItemTextTargets.forEach(el => (el.textContent = '\u2014'))
  }

  collapseResults() {
    const resultsCardTimeline = createTimeline()

    resultsCardTimeline
      .add(this.element, {
        onBegin: () => this.resultsTarget.classList.remove('max-h-none!'),
        duration: 600,
        maxWidth: 640,
        ease: cubicBezier(0.2, 0, 0.07, 1)
      })
      .add(
        this.resultsTarget,
        {
          duration: 600,
          maxHeight: 0,
          ease: cubicBezier(0.25, 0, 0.35, 1)
        },
        '-=600'
      )
  }

  expandResults() {
    if (this.resultsExpandedValue) return

    const resultsCardTimeline = createTimeline()

    resultsCardTimeline
      .add(this.element, {
        duration: 600,
        maxWidth: 1280,
        ease: cubicBezier(0.2, 0, 0.07, 1)
      })
      .add(
        this.resultsTarget,
        {
          duration: 600,
          maxHeight: this.resultsInnerTarget.getBoundingClientRect().height,
          ease: cubicBezier(0.25, 0, 0.35, 1),
          onComplete: () => this.resultsTarget.classList.add('max-h-none!')
        },
        '-=600'
      )
      .add(
        this.resultsInnerTarget,
        {
          duration: 200,
          opacity: [0, 1],
          onComplete: () => {
            this.resultsInnerTarget.style.pointerEvents = 'all'
            this.resultsExpandedValue = true
          }
        },
        '-=400'
      )
  }

  formatResponseValue(key) {
    const value = this.response[key]

    if (value === true) {
      return this.translationsValue.yes
    } else if (value === false) {
      return this.translationsValue.no
    } else if (value === null || value === undefined || value === '') {
      return '—'
    } else if (this.translationsValue[value]?.length) {
      return this.translationsValue[value]
    } else {
      return value
    }
  }

  populateResults() {
    // General
    this.fullNameTarget.textContent = this.formatResponseValue('full_name')
    this.genderTarget.textContent = this.formatResponseValue('gender')
    this.birthYearTarget.textContent = this.#formatBirthYear()

    this.stateTargets.forEach(target => {
      target.hidden = target.dataset.state !== this.response.state
      target.ariaHidden = target.dataset.state !== this.response.state
    })

    this.reasonTargets.forEach(target => {
      target.hidden = target.dataset.reason !== this.response.reason
      target.ariaHidden = target.dataset.reason !== this.response.reason
    })

    this.domainTarget.textContent = this.formatResponseValue('domain')
    this.userTarget.textContent = this.formatResponseValue('user')
    this.didYouMeanTarget.textContent = this.formatResponseValue('did_you_mean')

    // Attributes
    this.acceptAllTarget.textContent = this.formatResponseValue('accept_all')
    this.disposableTarget.textContent = this.formatResponseValue('disposable')
    this.freeTarget.textContent = this.formatResponseValue('free')
    this.roleTarget.textContent = this.formatResponseValue('role')
    this.tagTarget.textContent = this.formatResponseValue('tag')
    this.mailboxFullTarget.textContent =
      this.formatResponseValue('mailbox_full')
    this.noReplyTarget.textContent = this.formatResponseValue('no_reply')

    // Mail Server
    this.smtpProviderTarget.textContent =
      this.formatResponseValue('smtp_provider')
    this.mxRecordTarget.textContent = this.formatResponseValue('mx_record')
  }

  setRateLimitedState() {
    this.inputTarget.disabled = true
    this.buttonTarget.disabled = true
    this.clearResults()
    this.collapseResults()
    this.element.scrollIntoView(true)
    this.rateLimitPromptTarget.classList.remove('hidden')
    if (this.hasExamplePromptTarget) this.examplePromptTarget.remove()
  }

  updateMeter() {
    let markerOffset
    const { score, state } = this.response

    if (score >= 80) {
      markerOffset = `${score}%`
    } else if (score > 0 && score < 80) {
      markerOffset = `${Math.round(((score - 2) / 77) * 68 + 11)}%`
    } else if (score === 0) {
      markerOffset = '5%'
    } else if (score === null) {
      markerOffset = '0%'
    }

    this.meterMarkerTarget.style.left = markerOffset
    this.meterScoreTarget.textContent = score === null ? '?' : score

    // Sets the color of the meter marker
    this.stateValue = state
  }

  async getCaptchaToken() {
    return new Promise(resolve => {
      const check = () => {
        const token = turnstile.getResponse(this.captchaTarget)
        if (token) {
          turnstile.reset(this.captchaTarget)
          resolve(token)
        } else {
          setTimeout(check, 100)
        }
      }
      check()
    })
  }

  async verifyEmail(event) {
    if (event) event.preventDefault()
    clearTimeout(this.showLoadingTimeout)

    const email = this.inputTarget.value

    this.showLoadingTimeout = setTimeout(() => {
      this.inputTarget.disabled = true
      this.buttonTarget.disabled = true
      this.buttonTarget.dataset.loading = true
    }, 200)

    const url = new URL('https://api.emailable.com/v1/verify')
    const options = {
      method: 'POST',
      body: JSON.stringify({
        email: email,
        api_key: this.apiKeyValue,
        captcha_response: await this.getCaptchaToken()
      }),
      headers: { 'Content-Type': 'application/json' }
    }

    try {
      const response = await fetch(url, options)

      if (response?.status === 200) {
        this.rateLimitPromptTarget.classList.add('hidden')
        response.json().then(json => {
          this.response = json
          this.populateResults()
          this.updateMeter()
          this.expandResults()
          this.resultsForTextTarget.textContent = email
        })
      } else {
        this.setRateLimitedState()
      }
    } catch {
      this.setRateLimitedState()
    } finally {
      clearTimeout(this.showLoadingTimeout)
      this.inputTarget.disabled = false
      this.buttonTarget.disabled = false
      this.buttonTarget.dataset.loading = false
    }
  }

  verifyExampleEmail(event) {
    const email = event.target.textContent
    this.inputTarget.value = email
    this.verifyEmail()
  }

  #formatBirthYear() {
    const year = this.response.birth_year

    if (!year) return this.formatResponseValue('birth_year')

    return `${year} (~${new Date().getFullYear() - year})`
  }
}
